import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components/macro';

import MenuRoot from './index';
import { boxShadow } from 'styles/base-classes';
import { ILabelValuePair } from 'types/shared';

/**
 * Exported to apply custom focus-styles (with [aria-selected='true']) and hover-styles in child elements
 * Parent component can be used as a selector e.g. ${ItemWrapper}:hover &
 */
export const ItemWrapper = styled.li<{
  isCapitalized: boolean;
  isFirstUnderlined: boolean;
  disabled: boolean;
}>`
  text-transform: ${({ isCapitalized }): string =>
    isCapitalized ? 'capitalize' : 'initial'};

  ${({ disabled }): any =>
    disabled &&
    css`
      color: #ccc;
    `}

  ${({ isFirstUnderlined }): any =>
    isFirstUnderlined &&
    css`
      &:first-of-type {
        border-bottom: 1px solid #e9e9e9;
      }
    `}
`;

export const Figure = styled.figure<any>`
  ${boxShadow};
  position: absolute;
  z-index: 9999;
  margin: 0;
  text-align: left;
  min-width: 100%;
  background: white;
  ${({ changeRight }): string => changeRight && 'right: -7em!important'};
  ${({ maxWidth }): string => maxWidth && `max-width: ${maxWidth}`};
  overflow: hidden;
`;

export const MenuList = styled.ul<any>`
  background: white;
  ${({ isScrolled }): string => isScrolled && 'max-height: 38.0625rem'};
  overflow-y: auto;
`;

const Menu = (props: any): any => {
  const rootProps = { ...props };
  return (
    <MenuRoot {...rootProps}>
      {(renderProps): any => <Dropdown {...props} {...renderProps} />}
    </MenuRoot>
  );
};

interface IDropdownProps {
  items: any;
  selectedItem: string | Record<string, unknown>;
  Item: Function;
  ListLabel: any;
  itemToString: Function;
  getMenuProps: Function;
  getItemProps: Function;
  isIconDisplayed: boolean;
  firstItem: boolean;
  children: ReactNode;
  isScrolled: boolean;
  isTooltipEnabled: boolean;
  maxWidth: string;
  isMultipleSelection?: boolean;
  selectedItems?: any[];
  isFirstUnderlined?: boolean;
  isSubscriptionForm?: boolean;
  type?: string;
}

const getActiveItemState = ({
  item,
  isMultipleSelection,
  selectedItems,
  selectedItem,
  itemToString,
}: {
  item: any;
  isMultipleSelection: boolean;
  selectedItems: any[];
  selectedItem: any;
  itemToString: Function;
}): boolean => {
  if (isMultipleSelection && selectedItems.includes('all')) return true;

  return isMultipleSelection
    ? selectedItems && selectedItems.includes(itemToString(item))
    : itemToString(selectedItem) === itemToString(item);
};

const Dropdown: React.FC<IDropdownProps> = (props) => {
  const {
    items,
    selectedItem,
    Item,
    ListLabel,
    itemToString,
    getMenuProps,
    getItemProps,
    isIconDisplayed,
    firstItem,
    children,
    isScrolled,
    isTooltipEnabled,
    maxWidth,
    isMultipleSelection = false,
    selectedItems,
    isFirstUnderlined,
    isSubscriptionForm,
    type,
  } = props;

  const changeRight =
    firstItem &&
    (items[0].label.match('track|release') ||
      items[0].sortMetric === 'countryName');

  const selectedItemsValues = selectedItems?.map(
    (item: ILabelValuePair<string>) => item.value
  );

  return (
    <Figure
      data-menu-figure={true}
      changeRight={changeRight}
      maxWidth={maxWidth}
    >
      {ListLabel && (
        <figcaption>
          <ListLabel />
        </figcaption>
      )}
      <MenuList
        {...getMenuProps({ 'data-menu-list': true })}
        isScrolled={isScrolled}
      >
        {items.map((item: any, index: number): any => (
          <ItemWrapper
            key={Object.values(item).join('-')}
            {...getItemProps({
              'data-menu-list-item': true,
              key: Object.values(item).join('-'),
              disabled: item.disabled,
              index,
              item,
            })}
            isCapitalized={!isSubscriptionForm}
            isFirstUnderlined={isFirstUnderlined}
          >
            <Item
              item={item}
              index={index}
              isActive={getActiveItemState({
                isMultipleSelection,
                item,
                itemToString,
                selectedItem,
                selectedItems: selectedItemsValues || [],
              })}
              disabled={item.disabled}
              isIconDisplayed={isIconDisplayed}
              isTooltipEnabled={isTooltipEnabled}
              type={type}
              selectedItems={selectedItems}
            />
          </ItemWrapper>
        ))}
      </MenuList>
      {children}
    </Figure>
  );
};

export default Menu;
